import {
  MAXIMUM_REPORT_FILTER_URL_LENGTH,
  canonicalUuidSchema,
  encodedLength,
  encodedSearch,
  videoIdentityUrlValueSchema,
  videoSelectionSchema,
  type VideoSelection,
} from "@meta-video/contracts";

export interface WorkspaceSearchState {
  readonly savedViewId: string | null;
  readonly video: VideoSelection | null;
  readonly videoView: boolean;
}

export type WorkspaceSearchParseResult =
  | ({ readonly kind: "valid" } & WorkspaceSearchState)
  | { readonly kind: "invalid"; readonly message: string };

export type SavedViewSearchParseResult =
  | { readonly kind: "valid"; readonly savedViewId: string | null }
  | { readonly kind: "invalid"; readonly message: string };

export type VideoWorkspaceSearchParseResult =
  | {
      readonly kind: "valid";
      readonly video: VideoSelection | null;
      readonly videoView: boolean;
    }
  | { readonly kind: "invalid"; readonly message: string };

export class WorkspaceSearchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WorkspaceSearchError";
  }
}

const OWNED_KEYS = ["savedView", "video", "videoView"] as const;
const VIDEO_KEYS = ["video", "videoView"] as const;

export function parseWorkspaceSearch(input: string): WorkspaceSearchParseResult {
  if (typeof input !== "string") {
    return { kind: "invalid", message: "Workspace state requires the raw query string." };
  }
  const encoded = encodedSearch(input);
  if (encodedLength(encoded) > MAXIMUM_REPORT_FILTER_URL_LENGTH) {
    return { kind: "invalid", message: "Workspace URL exceeds the length limit." };
  }
  const savedView = parseSavedViewSearch(encoded);
  if (savedView.kind === "invalid") return savedView;
  const video = parseVideoWorkspaceSearch(encoded);
  if (video.kind === "invalid") return video;
  return {
    kind: "valid",
    savedViewId: savedView.savedViewId,
    video: video.video,
    videoView: video.videoView,
  };
}

function inputParams(input: string): URLSearchParams | null {
  if (typeof input !== "string") return null;
  const encoded = encodedSearch(input);
  if (encodedLength(encoded) > MAXIMUM_REPORT_FILTER_URL_LENGTH) return null;
  return new URLSearchParams(encoded);
}

function belongsToSearchKeyFamily(
  key: string,
  family: string,
): boolean {
  return key === family || key.startsWith(`${family}[`);
}

export function hasAmbiguousSearchKeys(
  params: URLSearchParams,
  keys: readonly string[],
): boolean {
  return [...params.keys()].some((key) =>
    keys.some((family) => key.startsWith(`${family}[`)))
    || keys.some((key) => params.getAll(key).length > 1);
}

export function parseSavedViewSearch(input: string): SavedViewSearchParseResult {
  const params = inputParams(input);
  if (params === null) {
    return { kind: "invalid", message: "Workspace URL is invalid or exceeds the length limit." };
  }
  if (hasAmbiguousSearchKeys(params, ["savedView"])) {
    return { kind: "invalid", message: "Saved-view state is ambiguous." };
  }
  const savedViewId = params.get("savedView");
  const parsedSavedView = savedViewId === null
    ? null
    : canonicalUuidSchema.safeParse(savedViewId);
  if (parsedSavedView !== null && !parsedSavedView.success) {
    return { kind: "invalid", message: "Saved-view state is invalid." };
  }
  return {
    kind: "valid",
    savedViewId: parsedSavedView === null ? null : parsedSavedView.data,
  };
}

export function parseVideoWorkspaceSearch(
  input: string,
): VideoWorkspaceSearchParseResult {
  const params = inputParams(input);
  if (params === null) {
    return { kind: "invalid", message: "Workspace URL is invalid or exceeds the length limit." };
  }
  if (hasAmbiguousSearchKeys(params, VIDEO_KEYS)) {
    return { kind: "invalid", message: "Focused-video state is ambiguous." };
  }
  const videoValue = params.get("video");
  const parsedVideo = videoValue === null
    ? null
    : videoIdentityUrlValueSchema.safeParse(videoValue);
  if (parsedVideo !== null && !parsedVideo.success) {
    return { kind: "invalid", message: "Focused-video state is invalid." };
  }

  const videoViewValue = params.get("videoView");
  if (videoViewValue !== null && videoViewValue !== "1") {
    return { kind: "invalid", message: "Video-detail view state is invalid." };
  }
  if (videoViewValue === "1" && parsedVideo === null) {
    return { kind: "invalid", message: "Video-detail view requires a focused video." };
  }

  return {
    kind: "valid",
    video: parsedVideo === null ? null : parsedVideo.data,
    videoView: videoViewValue === "1",
  };
}

export function removeSearchKeyFamilies(
  params: URLSearchParams,
  keys: readonly string[],
): boolean {
  let removed = false;
  for (const key of [...params.keys()]) {
    if (keys.some((family) => belongsToSearchKeyFamily(key, family))) {
      params.delete(key);
      removed = true;
    }
  }
  return removed;
}

function enforceCeiling(params: URLSearchParams): string {
  const encoded = params.toString();
  if (encodedLength(encoded) > MAXIMUM_REPORT_FILTER_URL_LENGTH) {
    throw new WorkspaceSearchError("Workspace URL exceeds the length limit.");
  }
  return encoded;
}

export function serializeSavedViewSearch(
  savedViewId: string | null,
  existing: string | URLSearchParams = "",
): string {
  const parsed = savedViewId === null ? null : canonicalUuidSchema.safeParse(savedViewId);
  if (parsed !== null && !parsed.success) {
    throw new WorkspaceSearchError("Saved-view state is invalid.");
  }
  const params = new URLSearchParams(encodedSearch(existing));
  removeSearchKeyFamilies(params, ["savedView"]);
  if (parsed !== null) params.set("savedView", parsed.data);
  return enforceCeiling(params);
}

export function removeSavedViewSearch(input: string): string {
  const params = new URLSearchParams(encodedSearch(input));
  removeSearchKeyFamilies(params, ["savedView"]);
  return params.toString();
}

export function serializeVideoWorkspaceSearch(
  state: Pick<WorkspaceSearchState, "video" | "videoView">,
  existing: string | URLSearchParams = "",
): string {
  const video = state.video === null ? null : videoSelectionSchema.safeParse(state.video);
  if (video !== null && !video.success) {
    throw new WorkspaceSearchError("Focused-video state is invalid.");
  }
  if (state.videoView && video === null) {
    throw new WorkspaceSearchError("Video-detail view requires a focused video.");
  }
  const params = new URLSearchParams(encodedSearch(existing));
  removeSearchKeyFamilies(params, VIDEO_KEYS);
  if (video !== null) params.set("video", JSON.stringify(video.data));
  if (state.videoView) params.set("videoView", "1");
  return enforceCeiling(params);
}

export function removeVideoWorkspaceSearch(input: string): string {
  const params = new URLSearchParams(encodedSearch(input));
  removeSearchKeyFamilies(params, VIDEO_KEYS);
  return params.toString();
}

export function serializeWorkspaceSearch(
  state: WorkspaceSearchState,
  existing: string | URLSearchParams = "",
): string {
  const savedViewId = state.savedViewId === null
    ? null
    : canonicalUuidSchema.safeParse(state.savedViewId);
  if (savedViewId !== null && !savedViewId.success) {
    throw new WorkspaceSearchError("Saved-view state is invalid.");
  }
  const video = state.video === null
    ? null
    : videoSelectionSchema.safeParse(state.video);
  if (video !== null && !video.success) {
    throw new WorkspaceSearchError("Focused-video state is invalid.");
  }
  if (state.videoView && video === null) {
    throw new WorkspaceSearchError("Video-detail view requires a focused video.");
  }
  const params = new URLSearchParams(encodedSearch(existing));
  removeSearchKeyFamilies(params, OWNED_KEYS);
  if (savedViewId !== null) params.set("savedView", savedViewId.data);
  if (video !== null) params.set("video", JSON.stringify(video.data));
  if (state.videoView) params.set("videoView", "1");
  return enforceCeiling(params);
}
